import Vue from 'vue'
import VueRouter from 'vue-router'
import { StorageService } from '@/helpers/storage'
import { AuthService } from '@/helpers/authService'

Vue.use(VueRouter)

const routes = [
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { title: 'Dashboard', requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../views/Settings.vue'),
    meta: { title: 'Settings', requiresAuth: true }
  },
  {
    path: '/purchase-orders',
    name: 'PurchaseOrders',
    component: () => import('../views/Inventory/PurchaseOrders.vue'),
    meta: { title: 'Purchase Orders', requiresAuth: true }
  },
  {
    path: '/purchase-orders/:id',
    name: 'PurchaseOrderDetails',
    component: () => import('../views/Inventory/PurchaseOrderDetails.vue'),
    meta: { title: 'Purchase Order', requiresAuth: true }
  },
  {
    path: '/items',
    name: 'Items',
    component: () => import('../views/Inventory/items.vue'),
    meta: { title: 'View Items', requiresAuth: true }
  },
  {
    path: '/landing',
    name: 'Landing',
    component: () => import('../views/Landing/LandingPage.vue'),
    meta: { title: 'Smart Inventory Management', requiresAuth: false }
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('../pages/CallbackPage.vue'),
    meta: { title: 'Authentication Callback', requiresAuth: true }
  },
  {
    path: '/tags',
    name: 'Tags',
    component: () => import('../views/Inventory/tags.vue'),
    meta: { title: 'View Tags', requiresAuth: true }
  },
  {
    path: '/categories',
    name: 'Categories',
    component: () => import('../views/Inventory/categories.vue'),
    meta: { title: 'View Categories', requiresAuth: true }
  },
  {
    path: '/folders',
    name: 'Folders',
    component: () => import('../views/Inventory/folders.vue'),
    meta: { title: 'View Folders', requiresAuth: true }
  },
  {
    path: '/finances',
    name: 'Requests',
    component: () => import('../views/ComingSoon.vue'),
    meta: { title: 'Test Page', subtitle: 'Test Page', requiresAuth: true }
  },
  {
    path: '/items/:id',
    name: 'ItemDetails',
    component: () => import('../views/Inventory/ItemDetails.vue'),
    meta: { title: 'Item Details', requiresAuth: true }
  },
  {
    path: '/folders/:id',
    name: 'FolderDetails',
    component: () => import('../views/Inventory/FolderDetails.vue'),
    meta: { title: 'Folder Details', requiresAuth: true }
  },
  {
    path: '/categories/:id',
    name: 'CategoryDetails',
    component: () => import('../views/Inventory/CategoryDetails.vue'),
    meta: { title: 'Category Details', requiresAuth: true }
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('../views/Reports.vue'),
    meta: { title: 'Reports & Analytics', requiresAuth: true }
  },
  {
    path: '/transaction/:id',
    name: 'TransactionDetails',
    component: () => import('../views/Inventory/TransactionDetails.vue'),
    meta: { title: 'Folder Details', requiresAuth: true }
  },
  {
    path: '/audit_logs',
    name: 'AuditLogs',
    component: () => import('../components/AuditLogViewer.vue'),
    meta: { title: 'Audit Logs', requiresAuth: true, requiresOwner: true }
  },
  {
    path: '/accounts',
    name: 'Accounts',
    component: () => import('../pages/AccountsPage.vue'),
    meta: { title: 'Accounts', requiresAuth: true, requiresOwner: true }
  },
  {
    path: '/batches',
    name: 'Batches',
    component: () => import('../views/Batches/BulkArchitecture.vue'),
    meta: { title: 'Bulk Architecture Setup', requiresAuth: true }
  },
  {
    path: '/',
    redirect: () => {
      const hasLoggedOut = localStorage.getItem('user_logged_out')
      if (hasLoggedOut === 'true') return '/landing'

      // Prefer Auth0 runtime state
      const auth0 = Vue.prototype.$auth0
      if (auth0 && !auth0.isLoading && auth0.isAuthenticated) {
        return '/dashboard'
      }

      // If using API key mode and it's valid, allow
      if (AuthService.notUsingSSO()) {
        return '/system'
      }

      return '/landing'
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  const auth0 = Vue.prototype.$auth0

  // Public routes
  if (to.meta.requiresAuth === false) {
    const hasLoggedOut = localStorage.getItem('user_logged_out')
    // If user is already authenticated and did not explicitly log out,
    // keep them in the app instead of showing landing page.
    if (
      to.path === '/landing' &&
      hasLoggedOut !== 'true' &&
      auth0 &&
      !auth0.isLoading &&
      auth0.isAuthenticated
    ) {
      return next('/dashboard')
    }
    return next()
  }

  // Enhanced wait for Auth0 initialization with longer timeout for slow networks
  const waitAuth0Ready = async () => {
    let tries = 0
    const maxTries = 100 // 5 seconds total

    // Wait for Auth0 instance to be created
    while (!auth0 && tries < 20) {
      await new Promise((r) => setTimeout(r, 50))
      tries += 1
    }

    if (!auth0) {
      console.warn('⚠️ Auth0 instance not available after waiting')
      return false
    }

    // Wait for Auth0 to finish loading
    tries = 0
    while (auth0.isLoading && tries < maxTries) {
      await new Promise((r) => setTimeout(r, 50))
      tries += 1
    }

    // Additional delay for localStorage writes to complete
    if (!auth0.isLoading) {
      await new Promise((r) => setTimeout(r, 150))
    }

    return true
  }

  const auth0Ready = await waitAuth0Ready()

  if (!auth0Ready || !auth0) {
    console.log('❌ Auth0 not ready, redirecting to landing')
    return next('/landing')
  }

  // Check Auth0 session state
  if (!auth0.isLoading && auth0.isAuthenticated) {
    try {
      localStorage.removeItem('user_logged_out')
    } catch {}

    // Role check (UI only)
    if (to.meta && to.meta.requiresOwner) {
      try {
        const roles = JSON.parse(localStorage.getItem('user_roles') || '[]')
        console.log('🔍 Checking Owner role requirement. User roles:', roles)
        if (!roles.includes('Owner')) {
          console.log(
            '❌ Owner role required but not found, redirecting to landing'
          )
          return next('/landing')
        }
      } catch (e) {
        console.log('❌ Error checking roles, redirecting to landing:', e)
        return next('/landing')
      }
    }
    return next()
  }

  // Check for explicit logout
  const hasLoggedOut = localStorage.getItem('user_logged_out')
  if (hasLoggedOut === 'true') {
    console.log('🚪 User explicitly logged out, redirecting to landing')
    StorageService.saveUseSSO(false)
    return next('/landing')
  }

  // Try silent authentication for refresh scenarios
  if (!auth0.isLoading && !auth0.isAuthenticated) {
    try {
      console.log('🔄 Attempting silent authentication...')
      const token = await auth0.getTokenSilently({
        ignoreCache: false,
        timeoutInSeconds: 10
      })
      if (token) {
        console.log('✅ Silent authentication successful')
        // Refresh the auth state
        await auth0.refreshState()
        // Small delay to ensure state is updated
        await new Promise((r) => setTimeout(r, 100))
        if (auth0.isAuthenticated) {
          try {
            localStorage.removeItem('user_logged_out')
          } catch {}
          return next()
        }
      }
    } catch (silentError) {
      console.log('🔕 Silent authentication failed:', silentError.message)
      // Don't log the full error as it may contain sensitive info
    }
  }

  // API key mode support (non-SSO)
  if (AuthService.notUsingSSO()) {
    console.log('🔑 Checking API key authentication...')
    const valid = await AuthService.isApiKeyValid()
    if (valid === true) {
      console.log('✅ API key valid, proceeding')
      return next()
    }
    console.log('❌ API key invalid')
  }

  // Default: not authenticated
  console.log('❌ No valid authentication found, redirecting to landing')
  console.log('Auth0 state:', {
    isLoading: auth0?.isLoading,
    isAuthenticated: auth0?.isAuthenticated,
    hasLoggedOut,
    usingSSO: AuthService.isUsingSSO()
  })
  return next('/landing')
})

export default router
